import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Facebook, Instagram, Linkedin } from 'lucide-react';
import { motion } from 'framer-motion';
import logoImage from '../../assets/images/logo.webp';
import { getMailtoLink, getTelLink, location as siteLocation, DEVELOPER_URL } from '@/config';

const XIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

interface PublicFooterProps {
  navLinks: { href: string; label: string }[];
  footerExtraLinks: { href: string; label: string }[];
  email: string;
  phone: string;
  phone2: string;
  address: string;
  facebookUrl: string;
  instagramUrl: string;
  xUrl: string;
  linkedinUrl: string;
}

export function PublicFooter({
  navLinks,
  footerExtraLinks,
  email,
  phone,
  phone2,
  address,
  facebookUrl,
  instagramUrl,
  xUrl,
  linkedinUrl,
}: PublicFooterProps) {
  const { t } = useTranslation();

  return (
    <footer className="bg-gradient-to-b from-[#1E293B] to-[#0F172A] text-white relative overflow-hidden print:hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent" />

      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          <FooterBrand />
          <FooterQuickLinks navLinks={navLinks} footerExtraLinks={footerExtraLinks} />
          <FooterGetInvolved />
          <FooterContact
            email={email}
            phone={phone}
            phone2={phone2}
            address={address}
            facebookUrl={facebookUrl}
            instagramUrl={instagramUrl}
            xUrl={xUrl}
            linkedinUrl={linkedinUrl}
          />
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-12 pt-8 border-t border-slate-800/50 flex flex-col md:flex-row items-center justify-between gap-4"
        >
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
            <p className="text-slate-400 text-sm">
              {t('public.footer.copyright', { year: new Date().getFullYear() })}
            </p>
            <span className="hidden sm:inline text-slate-500">•</span>
            <p className="text-slate-400 text-sm">
              {t('public.footer.poweredBy')}{' '}
              <a
                href={DEVELOPER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#FBBF24] hover:text-white transition-colors font-medium inline-flex items-center gap-1 group"
              >
                Agent
                <motion.span
                  className="inline-block"
                  whileHover={{ x: 2 }}
                >
                  →
                </motion.span>
              </a>
            </p>
          </div>
          <div className="flex items-center gap-6">
            <Link
              to="/privacy"
              className="text-slate-400 hover:text-[#FBBF24] transition-colors text-sm relative group"
            >
              {t('public.footer.privacy')}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#FBBF24] group-hover:w-full transition-all duration-300" />
            </Link>
            <Link
              to="/terms"
              className="text-slate-400 hover:text-[#FBBF24] transition-colors text-sm relative group"
            >
              {t('public.footer.terms')}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#FBBF24] group-hover:w-full transition-all duration-300" />
            </Link>
          </div>
        </motion.div>
      </div>
    </footer>
  );
}

function FooterBrand() {
  const { t } = useTranslation();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="lg:col-span-1"
    >
      <Link to="/" className="flex items-center gap-3 mb-5 group">
        <motion.div
          whileHover={{ scale: 1.05, rotate: 5 }}
          transition={{ duration: 0.3 }}
          className="relative"
        >
          <div className="absolute inset-0 bg-blue-500/20 rounded-2xl blur-xl group-hover:bg-blue-500/30 transition-all" />
          <img
            src={logoImage}
            alt="Logo de Youth Impact Mission Burundi"
            width={48}
            height={48}
            loading="lazy"
            decoding="async"
            className="h-12 w-auto relative z-10"
          />
        </motion.div>
        <span className="font-serif font-bold text-xl text-white group-hover:text-[#FBBF24] transition-colors">
          {t('public.footer.brand')}
        </span>
      </Link>
      <p className="text-slate-400 text-sm leading-relaxed mb-6">
        {t('public.footer.description')}
      </p>
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-medium">
        <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
        {t('public.footer.tagline', 'Transforming Communities')}
      </div>
    </motion.div>
  );
}

function FooterQuickLinks({
  navLinks,
  footerExtraLinks,
}: {
  navLinks: { href: string; label: string }[];
  footerExtraLinks: { href: string; label: string }[];
}) {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const handleClick = (e: React.MouseEvent, href: string) => {
    if (pathname === href) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: 0.1 }}
    >
      <h2 className="font-serif font-bold text-sm uppercase tracking-wider mb-5 text-white relative inline-block">
        {t('public.footer.quickLinks')}
        <span className="absolute -bottom-2 left-0 w-8 h-0.5 bg-gradient-to-r from-[#FBBF24] to-transparent" />
      </h2>
      <ul className="space-y-2.5">
        {[...navLinks, ...footerExtraLinks].map((link) => {
          const isActive = pathname === link.href;
          return (
            <li key={link.href}>
              <Link
                to={link.href}
                onClick={(e) => handleClick(e, link.href)}
                className={`text-sm inline-flex items-center gap-2 group transition-colors ${
                  isActive
                    ? 'text-[#FBBF24] font-medium'
                    : 'text-slate-400 hover:text-[#FBBF24]'
                }`}
              >
                <span
                  className={`h-0.5 bg-[#FBBF24] transition-all duration-200 ${
                    isActive ? 'w-2' : 'w-0 group-hover:w-2'
                  }`}
                />
                {link.label}
              </Link>
            </li>
          );
        })}
      </ul>
    </motion.div>
  );
}

function FooterGetInvolved() {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const handleClick = (e: React.MouseEvent, href: string) => {
    if (pathname === href) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const links = [
    { href: '/get-involved', label: t('public.footer.becomeAgent') },
    { href: '/donate', label: t('public.footer.makeDonation') },
    { href: '/contact', label: t('public.footer.partnerWithUs') },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <h2 className="font-serif font-bold text-sm uppercase tracking-wider mb-5 text-white relative inline-block">
        {t('public.footer.getInvolved')}
        <span className="absolute -bottom-2 left-0 w-8 h-0.5 bg-gradient-to-r from-[#FBBF24] to-transparent" />
      </h2>
      <ul className="space-y-2.5">
        {links.map((link) => {
          const isActive = pathname === link.href;
          return (
            <li key={link.href}>
              <Link
                to={link.href}
                onClick={(e) => handleClick(e, link.href)}
                className={`text-sm inline-flex items-center gap-2 group transition-colors ${
                  isActive
                    ? 'text-[#FBBF24] font-medium'
                    : 'text-slate-400 hover:text-[#FBBF24]'
                }`}
              >
                <span
                  className={`h-0.5 bg-[#FBBF24] transition-all duration-200 ${
                    isActive ? 'w-2' : 'w-0 group-hover:w-2'
                  }`}
                />
                {link.label}
              </Link>
            </li>
          );
        })}
      </ul>
    </motion.div>
  );
}

function FooterContact({
  email,
  phone,
  phone2,
  address,
  facebookUrl,
  instagramUrl,
  xUrl,
  linkedinUrl,
}: {
  email: string;
  phone: string;
  phone2: string;
  address: string;
  facebookUrl: string;
  instagramUrl: string;
  xUrl: string;
  linkedinUrl: string;
}) {
  const { t } = useTranslation();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: 0.3 }}
    >
      <h2 className="font-serif font-bold text-sm uppercase tracking-wider mb-5 text-white relative inline-block">
        {t('public.footer.contactUs')}
        <span className="absolute -bottom-2 left-0 w-8 h-0.5 bg-gradient-to-r from-[#FBBF24] to-transparent" />
      </h2>
      <ul className="space-y-2.5 text-sm text-slate-400">
        {address && <li className="leading-relaxed">{address}, {siteLocation.city}</li>}
        {email && (
          <li>
            <a href={getMailtoLink(email)} className="hover:text-[#FBBF24] transition-colors">
              {email}
            </a>
          </li>
        )}
        {phone && (
          <li>
            <a href={getTelLink(phone)} className="hover:text-[#FBBF24] transition-colors">
              {phone}
            </a>
          </li>
        )}
        {phone2 && (
          <li>
            <a href={getTelLink(phone2)} className="hover:text-[#FBBF24] transition-colors">
              {phone2}
            </a>
          </li>
        )}
      </ul>
      <SocialLinks
        facebookUrl={facebookUrl}
        instagramUrl={instagramUrl}
        xUrl={xUrl}
        linkedinUrl={linkedinUrl}
      />
    </motion.div>
  );
}

function SocialLinks({
  facebookUrl,
  instagramUrl,
  xUrl,
  linkedinUrl,
}: {
  facebookUrl: string;
  instagramUrl: string;
  xUrl: string;
  linkedinUrl: string;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex items-center gap-3 mt-6">
      {facebookUrl && (
        <motion.a
          href={facebookUrl}
          target="_blank"
          rel="noopener noreferrer"
          whileHover={{ scale: 1.15, y: -3 }}
          whileTap={{ scale: 0.95 }}
          className="group relative w-11 h-11 rounded-xl bg-slate-800/50 backdrop-blur-sm flex items-center justify-center text-slate-400 hover:text-white transition-all duration-300 border border-slate-700/50 hover:border-blue-500/50 overflow-hidden"
          aria-label={t('common.socialLinks.facebook')}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <Facebook className="w-5 h-5 relative z-10" />
        </motion.a>
      )}
      {instagramUrl && (
        <motion.a
          href={instagramUrl}
          target="_blank"
          rel="noopener noreferrer"
          whileHover={{ scale: 1.15, y: -3 }}
          whileTap={{ scale: 0.95 }}
          className="group relative w-11 h-11 rounded-xl bg-slate-800/50 backdrop-blur-sm flex items-center justify-center text-slate-400 hover:text-white transition-all duration-300 border border-slate-700/50 hover:border-pink-500/50 overflow-hidden"
          aria-label={t('common.socialLinks.instagram')}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <Instagram className="w-5 h-5 relative z-10" />
        </motion.a>
      )}
      {xUrl && (
        <motion.a
          href={xUrl}
          target="_blank"
          rel="noopener noreferrer"
          whileHover={{ scale: 1.15, y: -3 }}
          whileTap={{ scale: 0.95 }}
          className="group relative w-11 h-11 rounded-xl bg-slate-800/50 backdrop-blur-sm flex items-center justify-center text-slate-400 hover:text-white transition-all duration-300 border border-slate-700/50 hover:border-slate-400/50 overflow-hidden"
          aria-label={t('common.socialLinks.x')}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-slate-700 to-slate-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <XIcon className="w-5 h-5 relative z-10" />
        </motion.a>
      )}
      {linkedinUrl && (
        <motion.a
          href={linkedinUrl}
          target="_blank"
          rel="noopener noreferrer"
          whileHover={{ scale: 1.15, y: -3 }}
          whileTap={{ scale: 0.95 }}
          className="group relative w-11 h-11 rounded-xl bg-slate-800/50 backdrop-blur-sm flex items-center justify-center text-slate-400 hover:text-white transition-all duration-300 border border-slate-700/50 hover:border-blue-400/50 overflow-hidden"
          aria-label={t('common.socialLinks.linkedin')}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-blue-700 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <Linkedin className="w-5 h-5 relative z-10" />
        </motion.a>
      )}
    </div>
  );
}
